import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Table from './Table';

/**
 * PaginatedTable - A wrapper around Table that handles infinite scroll pagination internally
 *
 * @param {string} queryKey - Unique key for the query
 * @param {function} queryFn - Function to fetch data (page, limit, searchQuery) => Promise
 * @param {number} pageSize - Number of items per page (default: 50)
 * @param {function} transformData - Function to transform raw data to table format
 * @param {function} filterFn - DEPRECATED: Use backend filtering via searchQuery instead
 * @param {string} searchQuery - Search query string (passed to backend API)
 * @param {number} resetTrigger - Change this value to reset pagination (e.g., after mutations)
 * @param {function} onLoadingChange - Callback to notify parent of loading state
 * @param {object} tableProps - All other props to pass to the Table component
 */
function PaginatedTable({
  queryKey,
  queryFn,
  pageSize = 50,
  transformData,
  filterFn,
  searchQuery = '',
  resetTrigger = 0,
  refetchOnWindowFocus = true,
  onLoadingChange, // Callback to notify parent of loading state
  ...tableProps
}) {
  const queryClient = useQueryClient();

  // Pagination state
  const [page, setPage] = useState(1);
  const [allData, setAllData] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Fetch data with React Query
  const {
    data: response,
    isFetching,
    isLoading
  } = useQuery({
    queryKey: [queryKey, page, searchQuery],
    queryFn: () => queryFn(page, pageSize, searchQuery),
    refetchOnWindowFocus,
    refetchOnMount: true,
    staleTime: 0,
    keepPreviousData: true
  });

  // Notify parent of loading state changes
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isFetching || isLoadingMore);
    }
  }, [isFetching, isLoadingMore, onLoadingChange]);

  // Reset pagination when resetTrigger or searchQuery changes
  useEffect(() => {
    if (resetTrigger > 0) {
      console.log('🔄 Resetting pagination due to trigger change');
      setPage(1);
      setAllData([]);
      setHasMore(true);
      setIsLoadingMore(false);
      queryClient.invalidateQueries([queryKey]);
    }
  }, [resetTrigger, queryKey, queryClient]);

  // Reset pagination when search query changes
  useEffect(() => {
    console.log('🔍 Search query changed, resetting pagination');
    setPage(1);
    setAllData([]);
    setHasMore(true);
    setIsLoadingMore(false);
  }, [searchQuery]);

  // Handle data accumulation and hasMore calculation
  useEffect(() => {
    if (response?.data) {
      console.log('📊 Processing data:', {
        page,
        dataLength: response.data.length,
        totalData: response?.meta?.total || 0,
        pageSize
      });

      // Accumulate data
      if (page === 1) {
        setAllData(response.data);
      } else {
        setAllData((prev) => {
          // Filter out duplicates
          const existingIds = new Set(prev.map((item) => item.id));
          const newData = response.data.filter((item) => !existingIds.has(item.id));
          return [...prev, ...newData];
        });
      }

      // Calculate hasMore
      const totalData = response?.meta?.total || 0;
      const currentTotalData =
        page === 1 ? response.data.length : (page - 1) * pageSize + response.data.length;
      const isLastPage = response.data.length < pageSize;
      const hasMoreData = !isLastPage && currentTotalData < totalData;

      setHasMore(hasMoreData);
      setIsLoadingMore(false);
    }
  }, [response?.data, page, pageSize]);

  // Apply transformations (filtering is now done on backend via searchQuery)
  const processedData = useMemo(() => {
    let data = allData;

    // Apply data transformation if provided
    if (transformData) {
      data = transformData(data);
    }

    return data;
  }, [allData, transformData]);

  // Load more callback
  const loadMore = useCallback(() => {
    if (hasMore && !isFetching && !isLoadingMore) {
      console.log('📄 Loading more data, current page:', page);
      setIsLoadingMore(true);
      setPage((prevPage) => prevPage + 1);
    }
  }, [hasMore, isFetching, isLoadingMore, page]);

  return (
    <Table
      {...tableProps}
      data={processedData}
      enablePagination={true}
      hasMore={hasMore}
      loadMore={loadMore}
      dataLength={allData.length}
      isFetching={isFetching}
    />
  );
}

export default PaginatedTable;
